import React, { useState } from 'react';
import { AlertCircle, ArrowRight, CheckCircle, Layers, Loader, X } from 'lucide-react';
import { useTranslation } from '../../../../../contexts/I18nProvider';
import { ScanTextInput, NumericInput } from '../../../../common/inputs';
import { warehouseTransferService } from '../../../../../services/warehouseTransferService';
import { consolidationService } from '../../../../../services/consolidationService';
import { ConfirmLine, ExecutionResult } from '../types';
import { OpenCapacityArgs } from '../useCapacityUpdate';
import { clampQty, parsedQty } from '../utils';

interface ConfirmSheetProps {
    initialLines: ConfirmLine[];
    warehouseID: string;
    companyNo: string;
    onClose: () => void;
    /**
     * Kalles etter hver kjøring som bokførte minst én linje.
     * allDone=true → alle linjer i arket er bokført (parent lukker arket).
     */
    onExecuted: (result: ExecutionResult) => void;
    /** Åpne «Oppdater kapasitet» for linjens destinasjon + vare. */
    onUpdateCapacity?: (args: OpenCapacityArgs) => void;
}

/**
 * Felles bekreftelsesark (bottom-sheet) for alle tre visningene.
 *
 * Konsolidering BESTILLER kun: arket oppretter en KONSOLIDERING-merket utleggsordre
 * (StcIntRelOrder med ResType=50-kildelås per linje) via consolidationService.execute
 * (immediate). Selve flyttingen GJENNOMFØRES i Plassering-modulen som egen, bevisst
 * handling — først DA skrives StcTransaction/ItemBins (og reservasjonen frigjøres).
 * Ingenting eksporteres til ERP (finalize-vakten i StcIntRelOrderController hopper
 * over GR-eksport for relocations uten ordre-/GR-kobling).
 *
 * Retry er idempotent: linjer som alt ligger på en ordre (stcLineNo satt) opprettes
 * aldri på nytt — retry gjelder kun linjer som feilet opprettelse (de havner på en
 * egen, ny ordre).
 */
export const ConfirmSheet: React.FC<ConfirmSheetProps> = ({
    initialLines,
    warehouseID,
    companyNo,
    onClose,
    onExecuted,
    onUpdateCapacity
}) => {
    const { t } = useTranslation();
    const [lines, setLines] = useState<ConfirmLine[]>(initialLines);
    const [executing, setExecuting] = useState(false);
    const [globalError, setGlobalError] = useState<string | null>(null);
    const [orderRefs, setOrderRefs] = useState<number[]>([]);

    const isRunnable = (line: ConfirmLine): boolean =>
        line.status !== 'success' && parsedQty(line.quantity) > 0 && line.toBinCode.trim().length > 0;

    const runnableCount = lines.filter(isRunnable).length;
    const missingDestinationCount = lines.filter(
        (line) => line.status !== 'success' && parsedQty(line.quantity) > 0 && line.toBinCode.trim().length === 0
    ).length;
    const hasFailures = lines.some((line) => line.status === 'error');
    const canRun = runnableCount > 0;

    const updateLine = (id: string, patch: Partial<ConfirmLine>) => {
        setLines((prev) => prev.map((line) => (line.id === id ? { ...line, ...patch } : line)));
    };

    const handleExecute = async () => {
        setExecuting(true);
        setGlobalError(null);

        // Arbeidskopi slik at hele resultatet settes i én state-oppdatering til slutt.
        // Tidligere feilede linjer nullstilles til 'pending' slik at «Prøv igjen» kjører dem på nytt.
        const working: ConfirmLine[] = lines.map((line) =>
            line.status === 'success'
                ? { ...line }
                : { ...line, status: 'pending', errorMessage: undefined }
        );
        const toRun = working.filter(isRunnable);

        // 0) Valider overstyrte destinasjoner (kun for linjer som ikke alt er opprettet)
        for (const line of toRun) {
            if (line.stcLineNo !== undefined) continue;
            const destination = line.toBinCode.trim();
            if (destination !== line.originalToBinCode) {
                const validation = await warehouseTransferService.validateLocation(companyNo, destination);
                if (!validation.valid) {
                    line.status = 'error';
                    line.errorMessage = t(
                        'warehouse.modules.consolidation.errors.invalidDestination',
                        { '0': destination },
                        `Lokasjon "${destination}" ble ikke funnet`
                    );
                } else {
                    line.toBinCode = validation.locationName;
                }
            }
        }

        // OPPRETT utleggsordre for linjer som ikke har en fra før — og STOPP der.
        // Selve flyttingen gjennomføres i Plassering; ingenting bokføres her.
        // Retry etter delvis opprettelsesfeil gir en ny (egen) ordre for restene —
        // linjer som alt ligger på en ordre opprettes aldri på nytt.
        const toCreate = toRun.filter((line) => line.status !== 'error' && line.stcLineNo === undefined);
        const createdNow: ConfirmLine[] = [];
        let createdOrderNo: number | null = null;

        if (toCreate.length > 0) {
            try {
                const createResult = await consolidationService.execute({
                    warehouseID,
                    execution: 'immediate',
                    lines: toCreate.map((line) => ({
                        fromBinCode: line.fromBinCode,
                        toBinCode: line.toBinCode.trim(),
                        itemNo: line.itemNo,
                        quantity: parsedQty(line.quantity)
                    }))
                });

                for (const line of toCreate) {
                    const lineResult = createResult.results.find(
                        (r) => r.fromBinCode === line.fromBinCode && r.itemNo === line.itemNo
                    );
                    if (lineResult?.success && createResult.stcOrderNo && lineResult.stcLineNo !== undefined) {
                        line.stcOrderNo = createResult.stcOrderNo;
                        line.stcLineNo = lineResult.stcLineNo;
                        line.status = 'success';
                        line.errorMessage = undefined;
                        createdNow.push(line);
                    } else {
                        line.status = 'error';
                        line.errorMessage = lineResult?.errorMessage
                            || createResult.errorMessage
                            || t('warehouse.modules.consolidation.errors.createFailed', {}, 'Kunne ikke opprette utleggsordre');
                    }
                }

                if (createResult.stcOrderNo) {
                    createdOrderNo = createResult.stcOrderNo;
                    setOrderRefs((prev) => prev.includes(createResult.stcOrderNo!) ? prev : [...prev, createResult.stcOrderNo!]);
                }
            } catch (err) {
                const message = err instanceof Error
                    ? err.message
                    : t('warehouse.modules.consolidation.errors.createFailed', {}, 'Kunne ikke opprette utleggsordre');
                toCreate.forEach((line) => {
                    line.status = 'error';
                    line.errorMessage = message;
                });
                setGlobalError(message);
            }
        }

        setLines(working);
        setExecuting(false);

        if (createdNow.length > 0) {
            // Planlagt frigjorte kilder = linjer der hele det foreslåtte kvantumet er bestilt flyttet.
            // Frigjøringen skjer først når ordren utføres i Plassering.
            const plannedFreedBins = new Set(
                createdNow
                    .filter((line) => line.freesBin && parsedQty(line.quantity) >= line.maxQuantity)
                    .map((line) => line.fromBinCode)
            );
            const allDone = !working.some((line) => line.status === 'error');
            onExecuted({
                orderNos: createdOrderNo !== null ? [createdOrderNo] : [],
                lineCount: createdNow.length,
                plannedFreedCount: plannedFreedBins.size,
                allDone
            });
        }
    };

    return (
        <div className="fixed inset-0 z-50 flex items-end justify-center">
            {/* Backdrop */}
            <div
                className="absolute inset-0 bg-black bg-opacity-50"
                onClick={executing ? undefined : onClose}
            />

            {/* Bottom-sheet */}
            <div className="relative w-full max-w-2xl bg-white rounded-t-2xl shadow-xl max-h-[85vh] flex flex-col">
                {/* Header */}
                <div className="flex items-center justify-between p-4 border-b">
                    <h2 className="text-base font-semibold text-gray-900">
                        {t('warehouse.modules.consolidation.confirm.title', {}, 'Bekreft flytting')}
                    </h2>
                    <button
                        onClick={onClose}
                        disabled={executing}
                        className="p-2 text-gray-500 hover:bg-gray-100 rounded-lg min-h-[44px] min-w-[44px] flex items-center justify-center disabled:opacity-50"
                        aria-label={t('warehouse.modules.consolidation.confirm.cancel', {}, 'Avbryt')}
                    >
                        <X className="h-5 w-5" />
                    </button>
                </div>

                {/* Linjer */}
                <div className="flex-1 overflow-y-auto p-4 space-y-3">
                    {lines.map((line) => {
                        // Linjen låses når den er opprettet på en utleggsordre — antall og
                        // destinasjon ligger da fast på ordrelinjen, og retry fullfører samme linje.
                        const locked = executing || line.status === 'success' || line.stcLineNo !== undefined;
                        return (
                            <div
                                key={line.id}
                                className={`border rounded-lg p-3 ${
                                    line.status === 'success'
                                        ? 'bg-green-50 border-green-200'
                                        : line.status === 'error'
                                            ? 'bg-red-50 border-red-200'
                                            : 'bg-white border-gray-200'
                                }`}
                            >
                                <div className="flex items-start justify-between gap-2">
                                    <div className="min-w-0">
                                        <div className="font-mono text-sm font-semibold text-gray-900 break-all">
                                            {line.itemNo}
                                        </div>
                                        {line.description1 && (
                                            <div className="text-xs text-gray-500 break-words">{line.description1}</div>
                                        )}
                                    </div>
                                    {line.status === 'success' && (
                                        <CheckCircle className="h-5 w-5 text-green-600 flex-shrink-0" />
                                    )}
                                    {line.status === 'error' && (
                                        <AlertCircle className="h-5 w-5 text-red-600 flex-shrink-0" />
                                    )}
                                </div>

                                <div className="flex flex-wrap items-center gap-2 mt-2">
                                    {/* Fra-lokasjon (fast) */}
                                    <div className="min-w-0">
                                        <div className="text-xs text-gray-500">
                                            {t('warehouse.modules.consolidation.confirm.from', {}, 'Fra')}
                                        </div>
                                        <div className="font-mono text-sm text-gray-900 break-all">{line.fromBinCode}</div>
                                    </div>
                                    <ArrowRight className="h-4 w-4 text-gray-400 flex-shrink-0 mt-3" />
                                    {/* Til-lokasjon (overstyrbar) */}
                                    <div className="flex-1 min-w-[140px]">
                                        <div className="text-xs text-gray-500">
                                            {t('warehouse.modules.consolidation.confirm.to', {}, 'Til')}
                                        </div>
                                        <ScanTextInput
                                            value={line.toBinCode}
                                            onChange={(e) => updateLine(line.id, { toBinCode: e.target.value })}
                                            disabled={locked}
                                            placeholder={t('warehouse.modules.consolidation.confirm.destinationPlaceholder', {}, 'Skann/skriv destinasjon')}
                                            className="w-full p-2 border border-gray-300 rounded-lg font-mono text-sm focus:ring-2 focus:ring-cyan-500 focus:border-transparent min-h-[44px] disabled:bg-gray-100"
                                        />
                                    </div>
                                    {/* Antall (justerbart) */}
                                    <div>
                                        <div className="text-xs text-gray-500">
                                            {t('warehouse.modules.consolidation.confirm.quantity', {}, 'Antall')}
                                        </div>
                                        <div className="flex items-center gap-1">
                                            <NumericInput
                                                mode="decimal"
                                                quantity
                                                value={line.quantity}
                                                onChange={(e) => updateLine(line.id, { quantity: clampQty(e.target.value, line.maxQuantity) })}
                                                disabled={locked}
                                                className="w-20 p-2 border border-gray-300 rounded-lg text-right text-base focus:ring-2 focus:ring-cyan-500 focus:border-transparent min-h-[44px] disabled:bg-gray-100"
                                            />
                                            <span className="text-xs text-gray-400 whitespace-nowrap">/ {line.maxQuantity}</span>
                                        </div>
                                    </div>
                                </div>

                                {onUpdateCapacity && !locked && line.toBinCode.trim().length > 0
                                    && (line.capacityConfidence !== 'observed'
                                        || line.toBinCode.trim() !== line.originalToBinCode) && (
                                    <button
                                        onClick={() => onUpdateCapacity({
                                            binCode: line.toBinCode.trim(),
                                            itemNo: line.itemNo,
                                            itemDescription: line.description1
                                        })}
                                        className="mt-2 flex items-center gap-1 px-2 py-1.5 rounded-lg text-xs font-medium text-cyan-700 border border-cyan-200 hover:bg-cyan-50 min-h-[36px]"
                                        title={t('warehouse.modules.consolidation.capacity.updateHint', {}, 'Det er plass? Oppdater kapasiteten')}
                                    >
                                        <Layers className="h-3.5 w-3.5" />
                                        {t('warehouse.modules.consolidation.capacity.update', {}, 'Oppdater kapasitet')}
                                    </button>
                                )}

                                {line.status === 'error' && line.errorMessage && (
                                    <div className="text-xs text-red-700 mt-2 break-words">{line.errorMessage}</div>
                                )}
                            </div>
                        );
                    })}
                </div>

                {/* Footer */}
                <div className="p-4 border-t space-y-2">
                    <div className="text-xs text-gray-500">
                        {t('warehouse.modules.consolidation.confirm.executesInPutaway', {}, 'Dette oppretter en utleggsordre — flyttingen gjennomføres i Plassering, og saldo flyttes først da.')}
                    </div>
                    {globalError && (
                        <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-start text-sm text-red-800">
                            <AlertCircle className="h-4 w-4 mr-2 flex-shrink-0 mt-0.5" />
                            <span className="break-words">{globalError}</span>
                        </div>
                    )}
                    {orderRefs.length > 0 && (
                        <div className="text-xs text-gray-500">
                            {t('warehouse.modules.consolidation.confirm.orderRef', { '0': orderRefs.join(', ') }, `Utleggsordre #${orderRefs.join(', ')}`)}
                        </div>
                    )}
                    {missingDestinationCount > 0 && (
                        <div className="text-xs text-orange-600">
                            {t(
                                'warehouse.modules.consolidation.confirm.missingDestination',
                                { '0': missingDestinationCount },
                                `Mangler destinasjon på ${missingDestinationCount} linje(r)`
                            )}
                        </div>
                    )}
                    <div className="flex gap-2">
                        <button
                            onClick={onClose}
                            disabled={executing}
                            className="flex-1 py-3 rounded-lg border border-gray-300 text-gray-700 font-medium hover:bg-gray-50 min-h-[44px] disabled:opacity-50"
                        >
                            {t('warehouse.modules.consolidation.confirm.cancel', {}, 'Avbryt')}
                        </button>
                        <button
                            onClick={handleExecute}
                            disabled={executing || !canRun}
                            className={`flex-1 py-3 rounded-lg font-semibold min-h-[44px] flex items-center justify-center gap-2 transition-colors ${
                                !executing && canRun
                                    ? 'bg-green-500 text-white hover:bg-green-600'
                                    : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                            }`}
                        >
                            {executing && <Loader className="animate-spin h-4 w-4" />}
                            {executing
                                ? t('warehouse.modules.consolidation.confirm.executing', {}, 'Oppretter ordre…')
                                : hasFailures
                                    ? t('warehouse.modules.consolidation.confirm.retry', { '0': runnableCount }, `Prøv igjen (${runnableCount})`)
                                    : t('warehouse.modules.consolidation.confirm.execute', { '0': runnableCount }, `Opprett utleggsordre (${runnableCount})`)}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};
